import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Container from '@/components/@base/container';
import Button from '@/components/@base/button';
import { Text } from '@/components/@base/typography';
import { SIZE_ENUM } from '@/components/@base/@types';
import Spinner from '@/components/Spinner';
import localization from '@/constant/localization';
import { isRememberedBiometricDevice, isWebAuthnSupported, proveDeviceBiometric } from '@/lib/webauthn';
import { usePostDeviceCheck } from '@/services/hooks';

type GateState = 'checking' | 'open' | 'blocked' | 'verifying' | 'unsupported';

const BiometricGate = ({ children }: { children: ReactNode }) => {
  const { mutate } = usePostDeviceCheck();
  const [state, setState] = useState<GateState>('checking');
  const started = useRef(false);
  const options = useRef<string | null>(null);

  const runCheck = useCallback(() => {
    setState('checking');
    mutate(undefined, {
      onSuccess: async (res) => {
        if (!res.required || !res.options) {
          setState('open');
          return;
        }
        if (!isWebAuthnSupported()) {
          setState('unsupported');
          return;
        }
        options.current = res.options;
        setState(isRememberedBiometricDevice(res.options) ? 'open' : 'blocked');
      },
      onError: () => setState('open'),
    });
  }, [mutate]);

  const verifyDevice = async () => {
    if (!options.current) return;
    setState('verifying');
    try {
      await proveDeviceBiometric(options.current);
      setState('open');
    } catch {
      setState('blocked');
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    runCheck();
  }, [runCheck]);

  if (state === 'open') return <>{children}</>;

  return (
    <Container center className='fixed inset-0 z-50 flex-col bg-white px-6 dark:bg-grayDark-900'>
      {(state === 'checking' || state === 'verifying') && <Spinner />}
      {(state === 'blocked' || state === 'unsupported') && (
        <Container center className='w-full max-w-[420px] flex-col text-center'>
          <Text className='mb-4 text-xl text-black dark:text-white' semiBold>
            {localization.deviceCheckTitle}
          </Text>
          <Text className='mb-8 text-grayLight-650 dark:text-grayDark-75' size={SIZE_ENUM.MD}>
            {state === 'unsupported' ? localization.deviceCheckUnsupported : localization.deviceCheckBody}
          </Text>
          {state === 'blocked' && (
            <Button className='text-md h-[52px] w-full rounded-full' onClick={verifyDevice}>
              {localization.verifyWithBiometric}
            </Button>
          )}
        </Container>
      )}
    </Container>
  );
};

export default BiometricGate;
